import React from 'react';

export const WhatAreGardenApartments: React.FC = () => {
  const characteristics = [
    {
      icon: 'ri-building-2-line',
      title: 'Low-Rise Construction',
      description: 'Typically 2-3 stories without elevators, creating a more intimate community feel and lower maintenance costs.'
    },
    {
      icon: 'ri-plant-line',
      title: 'Abundant Landscaping',
      description: 'Extensive green spaces, gardens, and outdoor amenities that define the garden apartment aesthetic.'
    },
    {
      icon: 'ri-car-line',
      title: 'Ample Parking',
      description: 'Surface parking lots or carports providing convenient access for residents without structured parking expenses.'
    },
    {
      icon: 'ri-community-line',
      title: 'Suburban Setting',
      description: 'Usually located in suburban areas with lower density, offering residents a quieter living environment.'
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Unit Variety',
      description: 'Mix of one, two, and three-bedroom units appealing to diverse tenant demographics and income levels.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost-Effective Design',
      description: 'Efficient construction and operation costs make garden apartments attractive investment properties.'
    }
  ];

  return (
    <section id="why-garden-apartments" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Understanding Garden Apartments
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Garden apartments represent a unique property type in the multifamily real estate market, combining suburban charm with investment potential.
          </p>
        </div>

        <div className="mb-16">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl mb-8">
            <img
              src="https://public.readdy.ai/ai/img_res/21e106168291deb78c486e6fd8e7aaea.jpg"
              alt="Aerial View of Garden Apartment Complex"
              className="w-full h-[500px] object-cover object-top"
            />
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Garden apartments, also known as garden-style apartments, are a distinctive category of multifamily housing that emerged in the mid-20th century and remain popular today. These properties are characterized by their low-rise construction, typically ranging from two to three stories, and their emphasis on outdoor space and landscaping. Unlike high-rise apartment buildings or mid-rise complexes, garden apartments spread horizontally across larger land parcels, creating a more residential, neighborhood-like atmosphere.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The defining feature of garden apartments is their integration with outdoor spaces. These properties typically feature extensive landscaping, including lawns, gardens, trees, and sometimes courtyards or green spaces between buildings. This design philosophy creates an environment that feels less urban and more suburban, appealing to renters who want apartment living with a touch of nature and open space.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              From an investment perspective, garden apartments offer several advantages. Their construction costs are generally lower than high-rise buildings since they don't require elevators, complex structural systems, or expensive foundation work. Operating costs tend to be more predictable, and the suburban locations often provide stable tenant bases with families and professionals seeking affordable housing options outside urban cores.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {characteristics.map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl border border-orange-100 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-16 h-16 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                <i className={`${item.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {item.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};